import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { useCompanyInfo } from '../hooks/useAPI';

const CookiePolicyPage = () => {
  const { data: companyInfo } = useCompanyInfo();
  const company = companyInfo?.companyName || 'LionTek';
  const email = companyInfo?.email || 'hello@liontekglobal.com';

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Cookie Policy | LionTek</title>
        <meta name="description" content="LionTek's Cookie Policy — how we use cookies and how you can manage your preferences." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://liontekglobal.com/cookie-policy" />
      </Helmet>
      <Header companyInfo={companyInfo} />

      <main className="pt-24 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">

          {/* Header */}
          <div className="mb-10">
            <Link to="/" className="text-sm text-lion-blue hover:text-lion-yellow transition-colors mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="text-4xl font-poppins font-bold text-lion-blue mb-3">
              Cookie Policy
            </h1>
            <p className="text-gray-500 text-sm">Last updated: March 2026</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 space-y-10 text-gray-700 leading-relaxed">

            <section>
              <p>
                This Cookie Policy explains how {company} uses cookies and similar tracking
                technologies when you visit <strong>liontekglobal.com</strong>. It explains what
                these technologies are, why we use them, and your rights to control their use.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">1. What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your device when you visit a website.
                They are widely used to make websites work efficiently, improve user experience, and
                provide information to site owners. Cookies can be "session" cookies (deleted when
                you close your browser) or "persistent" cookies (which remain on your device until
                they expire or you delete them).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">2. Types of Cookies We Use</h2>
              <p className="mb-6">We use four categories of cookies on our website:</p>

              <div className="space-y-5">
                <div className="p-5 bg-gray-50 rounded-xl border-l-4 border-lion-blue">
                  <h3 className="font-semibold text-lion-blue mb-2">Necessary Cookies</h3>
                  <p className="text-sm">
                    These cookies are essential for the website to function and cannot be disabled.
                    They are usually set in response to actions you take, such as setting your
                    privacy preferences or filling in forms. You can set your browser to block these
                    cookies, but some parts of the site may not work as a result.
                  </p>
                </div>

                <div className="p-5 bg-gray-50 rounded-xl border-l-4 border-lion-yellow">
                  <h3 className="font-semibold text-lion-blue mb-2">Analytics Cookies</h3>
                  <p className="text-sm">
                    These cookies help us understand how visitors interact with our website by
                    collecting and reporting information anonymously. This helps us improve the
                    structure and content of our site. These cookies are only set with your consent.
                  </p>
                </div>

                <div className="p-5 bg-gray-50 rounded-xl border-l-4 border-blue-400">
                  <h3 className="font-semibold text-lion-blue mb-2">Marketing Cookies</h3>
                  <p className="text-sm">
                    Marketing cookies are used to track visitors across websites. The intention is
                    to display ads that are relevant and engaging for individual users. These cookies
                    are only set with your consent.
                  </p>
                </div>

                <div className="p-5 bg-gray-50 rounded-xl border-l-4 border-green-500">
                  <h3 className="font-semibold text-lion-blue mb-2">Functional Cookies</h3>
                  <p className="text-sm">
                    Functional cookies enable enhanced features and personalisation, such as live
                    chat, forms, and remembering your preferences. They may be set by us or by
                    third-party providers whose services we use. These cookies are only set with
                    your consent.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">3. Managing Your Cookie Preferences</h2>
              <p className="mb-4">
                When you first visit our website, you will see a cookie banner at the bottom of
                the screen. You can choose to accept all cookies, reject all non-essential cookies,
                or customise your preferences by category.
              </p>
              <p className="mb-4">
                You can update your preferences at any time by clearing your browser's local storage
                and revisiting the site — the cookie banner will reappear.
              </p>
              <p>
                You can also control cookies directly through your browser settings. Most browsers
                allow you to refuse cookies or delete them. However, blocking all cookies may affect
                the functionality of this and other websites you visit.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">4. Browser Opt-Out</h2>
              <p className="mb-3">
                You can opt out of cookies through your browser settings. Here are links to
                instructions for common browsers:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-sm">
                <li>Google Chrome — Settings → Privacy and Security → Cookies</li>
                <li>Mozilla Firefox — Options → Privacy &amp; Security → Cookies</li>
                <li>Safari — Preferences → Privacy → Cookies</li>
                <li>Microsoft Edge — Settings → Cookies and Site Permissions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">5. Changes to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time. Any changes will be posted on
                this page with an updated "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">6. Contact Us</h2>
              <p>
                If you have any questions about our use of cookies, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-lion-blue">{company}</p>
                <p>{companyInfo?.address || 'Africa'}</p>
                <p>
                  <a href={`mailto:${email}`} className="text-lion-blue hover:text-lion-yellow underline">
                    {email}
                  </a>
                </p>
              </div>
              <p className="mt-4">
                For more information about how we handle your personal data, please read our{' '}
                <Link to="/privacy-policy" className="text-lion-blue hover:text-lion-yellow underline">
                  Privacy Policy
                </Link>.
              </p>
            </section>

          </div>
        </div>
      </main>

      <Footer companyInfo={companyInfo} />
    </div>
  );
};

export default CookiePolicyPage;
